import React from "react";
import { Loader2 } from "lucide-react";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import BrandingCard from "@/components/partner/BrandingCard";
import SocialCategoryCard from "@/components/partner/SocialCategoryCard";
import PosConnectionCard from "@/components/partner/PosConnectionCard";
import VendorReviewsCard from "@/components/partner/VendorReviewsCard";
import VendorCoinInsightsCard from "@/components/partner/VendorCoinInsightsCard";
import SoundBoxCard from "@/components/partner/SoundBoxCard";
import VendorStatusPanel from "@/components/partner/VendorStatusPanel";
import EditHistoryBlock from "@/features/vendorPanel/EditHistoryBlock";
import StoresPanel from "@/components/partner/StoresPanel";
import { useVendorPanel } from "@/features/vendorPanel/useVendorPanel";
import { Hero, ValueProps, InviteCard, FaqSection } from "@/features/vendorPanel/MarketingBlocks";
import StepIndicator from "@/features/vendorPanel/StepIndicator";
import RegisterCard from "@/features/vendorPanel/RegisterCard";
import KycCard from "@/features/vendorPanel/KycCard";
import StripeConnectCard from "@/features/vendorPanel/StripeConnectCard";
import DoneCard from "@/features/vendorPanel/DoneCard";
import HowItWorksButton from "@/components/common/HowItWorksButton";

/**
 * Iter-280J — Partner Program landing + onboarding page.
 *
 * Route:  /partner-program              (marketing + generic apply CTA)
 *         /partner-program/:token       (invite-token prefilled onboarding)
 *
 * Flow (a partner clicks an invite email):
 *   1. GET  /api/classifieds/engagement/invites/{token}         → prefill
 *   2. POST /api/vendors/register                                → new user
 *      or   POST /api/classifieds/engagement/invites/{token}/claim  (logged-in)
 *   3. Upload KYC doc  → POST /api/vendors/kyc/upload  (multipart)
 *   4. Stripe Connect  → POST /api/vendors/stripe/connect  → { url }
 *
 * Iter 639 — split (behaviour IDENTICAL, every testid unchanged): the
 * wizard state lives in features/vendorPanel/useVendorPanel.js which
 * returns ONE bag (`const v = useVendorPanel()`); every wizard card
 * takes `{ v }`. This file is only the layout shell.
 */
export default function PartnerProgramPage() {
  const v = useVendorPanel();
  const { token, user, step, resumeDone } = v;

  // Iter 692 — owner email/SMS "Review it now" links land on
  // /partner-program#reviews. The cards above the Reviews card mount with
  // their own async queries and keep pushing it down, so a single scroll
  // lands short — re-scroll on every layout change until it settles.
  React.useEffect(() => {
    if (!user || window.location.hash !== "#reviews") return undefined;
    const scroll = () => {
      const el = document.getElementById("reviews");
      if (!el) return;
      // Re-scroll whenever layout growth above pushes it off target
      // (96px = scroll-mt-24). A transient "good" position isn't final —
      // keep correcting for the whole window.
      if (Math.abs(el.getBoundingClientRect().top - 96) > 48) {
        el.scrollIntoView({ block: "start" });
      }
    };
    scroll();
    const ro = new ResizeObserver(scroll);
    ro.observe(document.body);
    const iv = setInterval(scroll, 500);
    const stop = setTimeout(() => {
      ro.disconnect();
      clearInterval(iv);
    }, 8000);
    return () => {
      clearTimeout(stop);
      clearInterval(iv);
      ro.disconnect();
    };
  }, [user]);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="partner-program-page">
      <Seo
        title="Allsale Partner Program — grow your business with us"
        description="Join the Allsale Partner Program. Get a branded seller page, direct payouts and priority onboarding for verified NZ businesses."
      />
      <Breadcrumbs items={[{ label: "Partner Program" }]} />

      <Hero />

      <div className="mt-10 grid md:grid-cols-5 gap-8">
        <div className="md:col-span-2 space-y-6">
          <ValueProps />
          {token && (
            <InviteCard v={v} />
          )}
        </div>

        <div className="md:col-span-3">
          <StepIndicator v={v} />
          {/* Iter-643 — vendor video under the panel title, visible for
              unregistered AND registered vendors. */}
          <div className="mt-3 flex justify-end">
            <HowItWorksButton video="vendor" compact testid="howto-vendor-panel" />
          </div>

          {!resumeDone && (
            <div
              className="rounded-2xl bg-white shadow-card p-14 grid place-items-center"
              data-testid="partner-wizard-resuming"
            >
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}
          {resumeDone && step === 1 && <RegisterCard v={v} />}
          {resumeDone && step === 2 && <KycCard v={v} />}
          {resumeDone && step === 3 && <StripeConnectCard v={v} />}
          {resumeDone && step === 4 && <DoneCard v={v} />}
        </div>
      </div>

      <FaqSection />

      {/* Iter-412 §3 — multi-store: switcher above the vendor cards so
          everything below scopes to the active store (X-Store-Id). */}
      {user && <StoresPanel />}

      {/* Iter-288 — vendor self-service branding editor. Hidden if the
          signed-in user isn't a vendor (BrandingCard resolves that
          itself via GET /engagement/vendors/me). */}
      {user && <BrandingCard businessName={user?.business_name} />}

      {/* Iter-338 — vendor social pages + store category self-service.
          Resolves vendor-ness itself via GET /engagement/vendors/me. */}
      {user && <SocialCategoryCard />}

      {/* Iter-398 — Doshii POS connection (vendor self-service).
          Resolves vendor-ness itself via GET /engagement/vendors/me. */}
      {user && <PosConnectionCard />}

      {/* Iter-623 — live "today at a glance" owner status panel
          (KYC-approved owners only, above the sound box). */}
      {user && <VendorStatusPanel />}

      {/* Iter-616 — counter sound box device (KYC-approved owners only). */}
      {user && <SoundBoxCard />}

      {/* Iter 657 — who changed what, when (owner/staff/admin edits). */}
      {user && (
        <div className="mt-6">
          <EditHistoryBlock />
        </div>
      )}

      {/* Iter-442+ — vendor rewards & review approvals.
          Iter 692 — /partner-program#reviews (owner email/SMS links via
          /api/open/nz) lands here. */}
      {user && (
        <div id="reviews" className="scroll-mt-24">
          <VendorReviewsCard />
        </div>
      )}

      {/* Iter-600 §5 — vendor coin insights (8-week redemption table). */}
      {user && <VendorCoinInsightsCard />}

      <style>{`
        .pp-input{width:100%;background:#fff;border:1px solid var(--border-soft);border-radius:14px;padding:.7rem 1rem;font-size:.95rem;color:var(--text-primary);}
        .pp-input:focus{outline:none;border-color:var(--brand-blue);box-shadow:0 0 0 3px rgba(28,169,224,.12);}
        .pp-input.with-icon{padding-left:2.5rem;}
      `}</style>
    </div>
  );
}
